import { type Action, createStore } from 'react-sweet-state';
import { cloneDeep, uniqBy } from 'lodash';

import type { SavedTalkingPoint, TextAnnotation } from 'src/data/annotation';
import {
  createTalkingPoints,
  deleteTalkingPoint,
  queryTalkingPoints,
} from 'src/graphql/talkingPoints';
import type { ID } from 'src/lib/collection';
import { captureMessage } from 'src/lib/sentry';
import { type CommonContainerProps } from 'src/lib/sweetState';

export interface ContainerProps extends CommonContainerProps<State> {
  currentCustomerId: ID;
}

export interface State {
  talkingPoints: SavedTalkingPoint[];
  isLoading: boolean;
}

export type Actions = typeof ACTIONS;

const INITIAL_STATE: State = {
  talkingPoints: [],
  isLoading: false,
};

export const ACTIONS = {
  clearStore:
    (): Action<State> =>
    ({ setState }) => {
      setState(cloneDeep(INITIAL_STATE));
    },

  queryTalkingPoints:
    (): Action<State, ContainerProps> =>
    async ({ setState }, { currentCustomerId }) => {
      if (!currentCustomerId) return;

      setState({ isLoading: true });
      try {
        const result = await queryTalkingPoints({ customerId: currentCustomerId });
        setState({ talkingPoints: result });
      } catch (error) {
        captureMessage('Error while getting talking points', {
          level: 'warning',
          extra: {
            error: String(error),
            source: 'TalkingPointsStore -> queryTalkingPoints',
          },
        });
      } finally {
        setState({ isLoading: false });
      }
    },

  saveTalkingPoints:
    (talkingPoints: TextAnnotation[]): Action<State, ContainerProps, Promise<boolean>> =>
    async ({ getState, setState }, { currentCustomerId }) => {
      if (!currentCustomerId) return false;

      const { talkingPoints: currentTalkingPoints } = getState();

      try {
        const saved = await createTalkingPoints({
          customerId: currentCustomerId,
          talkingPoints,
        });

        // In case the talking points already exist,
        // we don't want to add them again, the save mutation will return the existing talking points
        const newItems = uniqBy([...saved, ...currentTalkingPoints], 'id');

        setState({ talkingPoints: newItems });
        return true;
      } catch (error) {
        captureMessage('Error while saving talking points', {
          level: 'warning',
          extra: {
            error: String(error),
            source: 'TalkingPointsStore -> saveTalkingPoints',
          },
        });
        return false;
      }
    },

  deleteTalkingPoint:
    (id: ID): Action<State, ContainerProps, Promise<boolean | undefined>> =>
    async ({ getState, setState }, { currentCustomerId }) => {
      if (!currentCustomerId) return;

      const { talkingPoints: currentTalkingPoints } = getState();

      try {
        await deleteTalkingPoint({ customerId: currentCustomerId, id });

        setState({ talkingPoints: currentTalkingPoints.filter((p) => p.id !== id) });
        return true;
      } catch (error) {
        captureMessage('Error while deleting talking point', {
          level: 'warning',
          extra: {
            error: String(error),
            source: 'TalkingPointsStore -> deleteTalkingPoint',
          },
        });
      }
      return false;
    },
};

export const TalkingPointsStore = createStore<State, Actions, ContainerProps>({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'talking-points-store',
});
